/**
 * Atomic execution engine — single-transaction execution with rollback semantics.
 *
 * Phase 2 (execute): apply compiled mutation steps sequentially in one PM
 * transaction, remap positions, evaluate assert steps post-mutation.
 *
 * Supports both single-block (range) and cross-block (span) targets.
 */

import type {
  MutationStep,
  AssertStep,
  TextRewriteStep,
  TextInsertStep,
  TextDeleteStep,
  StyleApplyStep,
  PlanReceipt,
  StepOutcome,
  TextStepData,
  AssertStepData,
  MutationsApplyInput,
  SetMarks,
  ReplacementPayload,
  Query,
  InlineRunPatchKey,
  UnderlinePatch,
  RFontsPatch,
  BorderPatch,
  StyleApplyInput,
} from '@superdoc/document-api';
import { INLINE_PROPERTY_BY_KEY } from '@superdoc/document-api';
import type { Editor } from '../../core/Editor.js';
import type { CompiledPlan } from './compiler.js';
import type {
  CompiledTarget,
  CompiledRangeTarget,
  CompiledSpanTarget,
  ExecuteContext,
} from './executor-registry.types.js';
import { getStepExecutor } from './executor-registry.js';
import { planError } from './errors.js';
import { mapAlignmentToJustificationForParagraph } from './paragraphs-wrappers.js';
import { closeHistory } from 'prosemirror-history';
import { yUndoPluginKey } from 'y-prosemirror';
import { checkRevision, getRevision } from './revision-tracker.js';
import { compilePlan } from './compiler.js';
import { getBlockIndex } from '../helpers/index-cache.js';
import { resolveBlockInsertionPos } from './create-insertion.js';
import { applyDirectMutationMeta, applyTrackedMutationMeta } from '../helpers/transaction-meta.js';
import { captureRunsInRange, resolveInlineStyle } from './style-resolver.js';
import { TOGGLE_MARK_SPECS } from './mark-directives.js';
import { mapBlockNodeType } from '../helpers/node-address-resolver.js';
import { resolveWithinScope, scopeByRange } from '../helpers/adapter-utils.js';
import { normalizeReplacementText } from './replacement-normalizer.js';
import { getWordChanges } from './word-diff.js';
import { calculateResolvedParagraphProperties } from '../../extensions/paragraph/resolvedPropertiesCache.js';
import { Fragment, Slice } from 'prosemirror-model';
import type { Mark as ProseMirrorMark, MarkType, Node as ProseMirrorNode, NodeType } from 'prosemirror-model';
import type { Transaction } from 'prosemirror-state';
import type { Mapping } from 'prosemirror-transform';
import { buildTextWithTabs, parentAllowsNodeAt, textBetweenWithTabs } from '../helpers/text-with-tabs.js';
import { compileSafeTextRegex } from '../helpers/safe-regex.js';
import { getFormattingStateAtPos } from '../../core/helpers/getMarksFromSelection.js';
import {
  TrackDeleteMarkName,
  TrackFormatMarkName,
  TrackInsertMarkName,
} from '../../extensions/track-changes/constants.js';

// ---------------------------------------------------------------------------
// Character-offset → document-position mapping
// ---------------------------------------------------------------------------

/**
 * Maps a character offset (within the text content of a range) to the
 * corresponding ProseMirror document position.  Needed because inline
 * node boundaries (run open/close) create gaps in the position space
 * that `textBetween` hides.
 *
 * Must mirror `textBetweenWithTabs`'s character accounting exactly — the diff
 * loop above computes prefix/suffix offsets against that string, then asks
 * this function to translate them back into PM positions. Any disagreement
 * (e.g. an atom that contributed a char on one side but not the other) maps
 * the edit to the wrong place. We count:
 *   - text nodes by char length (the obvious case),
 *   - `tab` nodes as 1 char (textBetweenWithTabs emits '\t'),
 *   - inline leaves declaring `leafText` by `leafText(node).length`
 *     (e.g. noBreakHyphen → '‑', length 1).
 * Everything else contributes 0 — matching the executor's call site, which
 * passes `leafFallback=''` so unknown leaves don't widen `originalText`.
 *
 * Atoms cannot be sliced mid-glyph, so when an offset lands strictly past an
 * atom's first char we resolve to the position immediately after the atom.
 */
export function charOffsetToDocPos(
  doc: ProseMirrorNode,
  rangeFrom: number,
  rangeTo: number,
  charOffset: number,
): number {
  let count = 0;
  let foundPos = -1;

  const resolveAtom = (pos: number, nodeSize: number, len: number) => {
    if (count + len < charOffset) return false;
    foundPos = charOffset === count ? pos : pos + nodeSize;
    return true;
  };

  doc.nodesBetween(rangeFrom, rangeTo, (node, pos) => {
    if (foundPos >= 0) return false;

    if (node.isText) {
      const textStart = Math.max(pos, rangeFrom);
      const textEnd = Math.min(pos + node.nodeSize, rangeTo);
      const textLen = textEnd - textStart;
      if (textLen <= 0) return false;
      if (count + textLen >= charOffset) {
        foundPos = textStart + (charOffset - count);
      }
      count += textLen;
      return false;
    }

    // tab nodes are non-leaf (content: 'inline*') but textBetweenWithTabs
    // surfaces them as '\t', so they consume one offset slot here too.
    if (node.type?.name === 'tab') {
      resolveAtom(pos, node.nodeSize, 1);
      count += 1;
      return false;
    }

    // Inline leaves with a `leafText` spec contribute their visible text.
    if (node.isLeaf && node.isInline) {
      const leafTextFn = (node.type?.spec as { leafText?: (n: ProseMirrorNode) => string } | undefined)?.leafText;
      if (typeof leafTextFn === 'function') {
        const leafText = leafTextFn(node);
        if (typeof leafText === 'string' && leafText.length > 0) {
          resolveAtom(pos, node.nodeSize, leafText.length);
          count += leafText.length;
        }
      }
      return false;
    }

    return true; // descend into non-text, non-leaf nodes
  });

  return foundPos >= 0 ? foundPos : rangeTo;
}

// ---------------------------------------------------------------------------
// Style resolution helpers
// ---------------------------------------------------------------------------

/** Default inline policy when style is omitted from text.rewrite. */
const DEFAULT_INLINE_POLICY: import('@superdoc/document-api').InlineStylePolicy = {
  mode: 'preserve',
  onNonUniform: 'majority',
};
const CORE_SET_MARK_KEYS = ['bold', 'italic', 'underline', 'strike'] as const;
const DEBUG_TEXT_REWRITE =
  typeof process !== 'undefined' && typeof process.env?.SUPERDOC_DEBUG_TEXT_REWRITE === 'string'
    ? process.env.SUPERDOC_DEBUG_TEXT_REWRITE === '1'
    : false;
const TRACKED_REWRITE_BATCH_META = 'documentApiTrackedRewriteBatch';
const TRACKED_REWRITE_CHANGED_META = 'documentApiTrackedRewriteChanged';
// Keep <=9-change single rewrites granular so unchanged anchors stay live. The
// SD-3478 large-rewrite regressions cross this boundary and need coarse replace
// to avoid remapped tracked diff steps leaving corrupted anchors in accepted text.
const MAX_GRANULAR_TRACKED_REWRITE_CHANGES = 9;

function debugTextRewrite(message: string, details?: Record<string, unknown>): void {
  if (!DEBUG_TEXT_REWRITE) return;
  console.error('[text-rewrite]', message, details ?? {});
}

function isTrackedMutationTransaction(tr: Transaction): boolean {
  return tr.getMeta?.('forceTrackChanges') === true;
}

function isTrackedRewriteBatchTransaction(tr: Transaction): boolean {
  // The first changed rewrite can still use granular word-diff safely; later
  // changed rewrites in the same tracked batch use a coarse replace so previous
  // tracked insert/delete maps do not corrupt their positions.
  return (
    isTrackedMutationTransaction(tr) && tr.getMeta(TRACKED_REWRITE_BATCH_META) === true && hasTrackedRewriteChanged(tr)
  );
}

function shouldUseCoarseSingleTrackedRewrite(tr: Transaction, wordChangeCount: number): boolean {
  return isTrackedMutationTransaction(tr) && wordChangeCount > MAX_GRANULAR_TRACKED_REWRITE_CHANGES;
}

function hasTrackedRewriteChanged(tr: Transaction): boolean {
  return tr.getMeta?.(TRACKED_REWRITE_CHANGED_META) === true;
}

function markTrackedRewriteChanged(tr: Transaction): void {
  if (!isTrackedMutationTransaction(tr)) return;
  tr.setMeta?.(TRACKED_REWRITE_CHANGED_META, true);
}

function countTextRewriteTargets(compiled: CompiledPlan): number {
  return compiled.mutationSteps.reduce((count, compiledStep) => {
    return compiledStep.step.op === 'text.rewrite' ? count + compiledStep.targets.length : count;
  }, 0);
}

export function applyMutationPlanMeta(tr: Transaction, compiled: CompiledPlan, changeMode: 'direct' | 'tracked'): void {
  if (changeMode === 'tracked') {
    applyTrackedMutationMeta(tr);
    if (countTextRewriteTargets(compiled) > 1) tr.setMeta(TRACKED_REWRITE_BATCH_META, true);
  } else {
    applyDirectMutationMeta(tr);
  }
}

type StructuredTextPayload = {
  blocks: string[];
  splitBefore: boolean;
  splitAfter: boolean;
};

type InlineWrapperSpec = {
  type: NodeType;
  attrs: Record<string, unknown>;
  marks: readonly ProseMirrorMark[];
};

function asProseMirrorMarks(marks: readonly unknown[]): readonly ProseMirrorMark[] {
  return marks as readonly ProseMirrorMark[];
}

const TRACKED_REVIEW_MARK_NAMES = new Set([TrackInsertMarkName, TrackDeleteMarkName, TrackFormatMarkName]);

function hasTrackedReviewMark(marks: readonly ProseMirrorMark[] | undefined): boolean {
  return Boolean(marks?.some((mark) => TRACKED_REVIEW_MARK_NAMES.has(mark.type.name)));
}

function rangeTouchesTrackedReviewState(doc: ProseMirrorNode, from: number, to: number): boolean {
  let found = false;

  doc.nodesBetween(from, to, (node, pos) => {
    if (found) return false;

    if (node.isText) {
      const textStart = Math.max(from, pos);
      const textEnd = Math.min(to, pos + node.nodeSize);
      if (textStart >= textEnd) return false;
    }

    if ((node.isText || node.isInline) && hasTrackedReviewMark(node.marks as readonly ProseMirrorMark[] | undefined)) {
      found = true;
      return false;
    }
  });

  return found;
}

function resolveMarksForRange(editor: Editor, target: CompiledRangeTarget, step: MutationStep): readonly unknown[] {
  if (step.op !== 'text.rewrite') return [];
  const rewriteStep = step as TextRewriteStep;
  const policy = rewriteStep.args.style?.inline ?? DEFAULT_INLINE_POLICY;

  // capturedStyle is populated at compile time for selection targets.
  // Fall back to live capture only for range targets with a real blockId.
  if (target.capturedStyle) {
    return resolveInlineStyle(editor, target.capturedStyle, policy, step.id);
  }

  // Synthetic blockId ('__selection__') means both selection endpoints were
  // nodeEdge anchors with no text block — no inline style to preserve.
  if (target.blockId === '__selection__') return [];

  const captured = captureRunsInRange(editor, toAbsoluteBlockPos(editor, target.blockId), target.from, target.to);
  return resolveInlineStyle(editor, captured, policy, step.id);
}

function toAbsoluteBlockPos(editor: Editor, blockId: string): number {
  const index = getBlockIndex(editor);
  const candidate = index.candidates.find((c) => c.nodeId === blockId);
  if (!candidate) throw planError('TARGET_NOT_FOUND', `block "${blockId}" not found in style capture fallback`);
  return candidate.pos;
}

function buildMarksFromSetMarks(editor: Editor, setMarks?: SetMarks): readonly ProseMirrorMark[] {
  if (!setMarks) return [];
  const { schema } = editor.state;
  const marks: ProseMirrorMark[] = [];

  for (const key of CORE_SET_MARK_KEYS) {
    const directive = setMarks[key];
    if (!directive) continue;

    const spec = TOGGLE_MARK_SPECS[key];
    const markType = schema.marks[spec.schemaName];
    if (!markType) continue;

    if (directive === 'on') {
      marks.push(spec.createOn(markType as unknown as { create: MarkType['create'] }) as unknown as ProseMirrorMark);
      continue;
    }
    if (directive === 'off') {
      marks.push(markType.create(spec.offAttrs));
    }
    // `clear` intentionally emits no mark.
  }

  return marks;
}

// ---------------------------------------------------------------------------
// Shared inline style patch — registry-driven mark + runAttribute mutation
// ---------------------------------------------------------------------------

type InlineRunPatch = StyleApplyInput['inline'];
type TextStylePatchKey = 'color' | 'fontSize' | 'fontFamily' | 'letterSpacing' | 'vertAlign' | 'position';
type TextStylePatch = Partial<Pick<InlineRunPatch, TextStylePatchKey>> & {
  /** Derived from `caps` boolean — mapped to the textStyle mark's `textTransform` attribute. */
  textTransform?: string | null;
};

interface InlineTextSegment {
  from: number;
  to: number;
  marks: readonly ProseMirrorMark[];
}

interface OverlappingRun {
  pos: number;
}

const BOOLEAN_INLINE_MARK_KEYS = ['bold', 'italic', 'strike'] as const;
const TEXT_STYLE_KEYS = ['color', 'fontSize', 'fontFamily', 'letterSpacing', 'vertAlign', 'position'] as const;
const PRESERVE_RUN_PROPERTIES_META_KEY = 'sdPreserveRunPropertiesKeys';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isDeepEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function normalizeHexColor(color: string): string {
  const trimmed = color.trim();
  if (trimmed.startsWith('#')) return trimmed;
  if (/^[0-9a-fA-F]{3,8}$/.test(trimmed)) return `#${trimmed}`;
  return trimmed;
}

function toPointString(value: number): string {
  return `${value}pt`;
}

function toHalfPoints(value: number): number {
  return Math.round(value * 2);
}

function capsToTextTransform(caps: boolean | null): string | null {
  if (caps === null) return null;
  return caps ? 'uppercase' : 'none';
}

function collectInlineTextSegments(doc: ProseMirrorNode, absFrom: number, absTo: number): InlineTextSegment[] {
  const segments: InlineTextSegment[] = [];

  doc.nodesBetween(absFrom, absTo, (node, pos) => {
    if (!node.isText) return;

    const from = Math.max(absFrom, pos);
    const to = Math.min(absTo, pos + node.nodeSize);
    if (from >= to) return;

    segments.push({
      from,
      to,
      marks: (node.marks ?? []) as readonly ProseMirrorMark[],
    });
  });

  return segments;
}

function findMark(marks: readonly ProseMirrorMark[], markTypeName: string): ProseMirrorMark | undefined {
  return marks.find((mark) => mark.type.name === markTypeName);
}

function compactAttrs(attrs: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(attrs).filter(([, value]) => value !== null && value !== undefined));
}

function applyTriStateMarkPatch(
  tr: Transaction,
  markType: MarkType | undefined,
  absFrom: number,
  absTo: number,
  value: boolean | null | undefined,
): boolean {
  if (value === undefined || !markType) return false;

  if (value === null) {
    tr.removeMark(absFrom, absTo, markType);
    return true;
  }

  if (value === true) {
    tr.addMark(absFrom, absTo, markType.create());
    return true;
  }

  tr.addMark(absFrom, absTo, markType.create({ value: '0' }));
  return true;
}

function applyHighlightPatch(
  tr: Transaction,
  markType: MarkType | undefined,
  absFrom: number,
  absTo: number,
  value: string | null | undefined,
): boolean {
  if (value === undefined || !markType) return false;

  if (value === null) {
    tr.removeMark(absFrom, absTo, markType);
    return true;
  }

  tr.addMark(absFrom, absTo, markType.create({ color: value }));
  return true;
}

function mergeTextStyleAttrs(currentAttrs: Record<string, unknown>, patch: TextStylePatch): Record<string, unknown> {
  const next = { ...currentAttrs };

  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;

    if (value === null) {
      delete next[key];
      continue;
    }

    if (key === 'color' && typeof value === 'string') {
      next.color = normalizeHexColor(value);
      continue;
    }
    if ((key === 'fontSize' || key === 'letterSpacing' || key === 'position') && typeof value === 'number') {
      next[key] = toPointString(value);
      continue;
    }

    next[key] = value;
  }

  return compactAttrs(next);
}

function applyTextStylePatch(
  tr: Transaction,
  markType: MarkType | undefined,
  absFrom: number,
  absTo: number,
  patch: TextStylePatch,
): boolean {
  if (!markType) return false;
  if (Object.keys(patch).length === 0) return false;

  const segments = collectInlineTextSegments(tr.doc, absFrom, absTo);
  let changed = false;

  for (const segment of segments) {
    const existingMark = findMark(segment.marks, markType.name);
    const existingAttrs = (existingMark?.attrs ?? {}) as Record<string, unknown>;
    const nextAttrs = mergeTextStyleAttrs(existingAttrs, patch);
    const hadMark = Boolean(existingMark);
    const shouldKeepMark = Object.keys(nextAttrs).length > 0;

    if (!hadMark && !shouldKeepMark) continue;
    if (hadMark && shouldKeepMark && isDeepEqual(existingAttrs, nextAttrs)) continue;

    tr.removeMark(segment.from, segment.to, markType);
    if (shouldKeepMark) {
      tr.addMark(segment.from, segment.to, markType.create(nextAttrs));
    }
    changed = true;
  }

  return changed;
}

function mergeUnderlineAttrs(currentAttrs: Record<string, unknown>, patchValue: InlineRunPatch['underline']) {
  if (patchValue === null) return null;

  const next = { ...currentAttrs };

  if (patchValue === true) {
    if (!next.underlineType || next.underlineType === 'none') next.underlineType = 'single';
    return compactAttrs(next);
  }

  if (patchValue === false) {
    next.underlineType = 'none';
    return compactAttrs(next);
  }

  const patch = patchValue as UnderlinePatch;

  if (patch.style !== undefined) {
    if (patch.style === null) delete next.underlineType;
    else next.underlineType = patch.style;
  }
  if (patch.color !== undefined) {
    if (patch.color === null) delete next.underlineColor;
    else next.underlineColor = normalizeHexColor(patch.color);
  }
  if (patch.themeColor !== undefined) {
    if (patch.themeColor === null) delete next.underlineThemeColor;
    else next.underlineThemeColor = patch.themeColor;
  }

  if (!next.underlineType && (next.underlineColor || next.underlineThemeColor)) {
    next.underlineType = 'single';
  }

  const compacted = compactAttrs(next);
  return Object.keys(compacted).length > 0 ? compacted : null;
}

function applyUnderlinePatch(
  tr: Transaction,
  markType: MarkType | undefined,
  absFrom: number,
  absTo: number,
  patchValue: InlineRunPatch['underline'],
): boolean {
  if (patchValue === undefined || !markType) return false;

  const segments = collectInlineTextSegments(tr.doc, absFrom, absTo);
  let changed = false;

  for (const segment of segments) {
    const existingMark = findMark(segment.marks, markType.name);
    const existingAttrs = (existingMark?.attrs ?? {}) as Record<string, unknown>;
    const nextAttrs = mergeUnderlineAttrs(existingAttrs, patchValue);
    const hadMark = Boolean(existingMark);

    if (!hadMark && nextAttrs === null) continue;
    if (hadMark && nextAttrs !== null && isDeepEqual(existingAttrs, nextAttrs)) continue;

    tr.removeMark(segment.from, segment.to, markType);
    if (nextAttrs !== null) {
      tr.addMark(segment.from, segment.to, markType.create(nextAttrs));
    }
    changed = true;
  }

  return changed;
}

function collectOverlappingRuns(
  doc: ProseMirrorNode,
  runType: NodeType,
  absFrom: number,
  absTo: number,
): OverlappingRun[] {
  const runs: OverlappingRun[] = [];

  doc.nodesBetween(absFrom, absTo, (node, pos) => {
    if (node.type !== runType) return true;

    const runFrom = pos + 1;
    const runTo = pos + node.nodeSize - 1;
    if (Math.max(absFrom, runFrom) < Math.min(absTo, runTo)) {
      runs.push({ pos });
    }
    return false;
  });

  return runs;
}

function normalizeRFontsPatch(patch: RFontsPatch): Record<string, unknown> {
  const next: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(patch)) {
    if (key === 'csTheme') {
      next.cstheme = value;
      continue;
    }
    next[key] = value;
  }

  return next;
}

function normalizeBorderPatch(patch: BorderPatch): Record<string, unknown> {
  const next: Record<string, unknown> = {};

  if (patch.val !== undefined) next.val = patch.val;
  if (patch.sz !== undefined) next.size = patch.sz;
  if (patch.color !== undefined) next.color = patch.color;
  if (patch.space !== undefined) next.space = patch.space;

  return next;
}

function normalizeRunAttributePatchValue(key: InlineRunPatchKey, value: unknown): unknown {
  if (value === null) return null;

  switch (key) {
    case 'border':
      return normalizeBorderPatch(value as BorderPatch);
    case 'rFonts':
      return normalizeRFontsPatch(value as RFontsPatch);
    case 'fontSizeCs':
      return toHalfPoints(value as number);
    case 'kerning':
      return toHalfPoints(value as number);
    case 'stylisticSets':
      return Array.isArray(value) ? value.map((entry) => ({ ...entry })) : value;
    default:
      return value;
  }
}

function mergeRunAttributeValue(currentValue: unknown, patchValue: unknown): { changed: boolean; nextValue?: unknown } {
  if (patchValue === null) {
    return { changed: currentValue !== undefined };
  }

  if (Array.isArray(patchValue)) {
    if (isDeepEqual(currentValue, patchValue)) return { changed: false, nextValue: currentValue };
    return { changed: true, nextValue: patchValue };
  }

  if (isRecord(patchValue)) {
    const merged = isRecord(currentValue) ? { ...currentValue } : {};
    for (const [key, value] of Object.entries(patchValue)) {
      if (value === null || value === undefined) delete merged[key];
      else merged[key] = value;
    }
    if (Object.keys(merged).length === 0) {
      return { changed: currentValue !== undefined };
    }
    if (isDeepEqual(currentValue, merged)) return { changed: false, nextValue: currentValue };
    return { changed: true, nextValue: merged };
  }

  if (Object.is(currentValue, patchValue)) return { changed: false, nextValue: currentValue };
  return { changed: true, nextValue: patchValue };
}

function buildRunAttributeUpdates(inline: InlineRunPatch): Record<string, unknown> {
  const updates: Record<string, unknown> = {};
  const keys = Object.keys(inline) as InlineRunPatchKey[];

  for (const key of keys) {
    const entry = INLINE_PROPERTY_BY_KEY[key];
    if (!entry || entry.storage !== 'runAttribute') continue;

    const value = inline[key];
    if (value === undefined) continue;

    const carrier = entry.carrier;
    const runPropertyKey =
      carrier.storage === 'runAttribute'
        ? carrier.runPropertyKey
        : (() => {
            throw planError('INTERNAL_ERROR', `Invalid carrier for runAttribute key "${key}"`);
          })();

    updates[runPropertyKey] = normalizeRunAttributePatchValue(key, value);
  }

  return updates;
}

function applyRunAttributePatch(
  tr: Transaction,
  runType: NodeType | undefined,
  absFrom: number,
  absTo: number,
  updates: Record<string, unknown>,
): boolean {
  if (!runType) return false;
  if (Object.keys(updates).length === 0) return false;

  const overlappingRuns = collectOverlappingRuns(tr.doc, runType, absFrom, absTo).sort(
    (left, right) => right.pos - left.pos,
  );
  if (overlappingRuns.length === 0) return false;

  if (Object.prototype.hasOwnProperty.call(updates, 'fontFamily')) {
    tr.setMeta(PRESERVE_RUN_PROPERTIES_META_KEY, [{ key: 'fontFamily', preferExisting: true }]);
  }

  let changed = false;

  for (const run of overlappingRuns) {
    const runPos = tr.mapping.map(run.pos, 1);
    const runNode = tr.doc.nodeAt(runPos);
    if (!runNode || runNode.type !== runType) continue;

    const mappedFrom = tr.mapping.map(absFrom, 1);
    const mappedTo = tr.mapping.map(absTo, -1);
    const runContentFrom = runPos + 1;
    const runContentTo = runPos + runNode.nodeSize - 1;

    const patchFrom = Math.max(mappedFrom, runContentFrom);
    const patchTo = Math.min(mappedTo, runContentTo);
    if (patchFrom >= patchTo) continue;

    const currentRunProperties = isRecord(runNode.attrs?.runProperties)
      ? { ...(runNode.attrs.runProperties as Record<string, unknown>) }
      : {};
    const currentStyleKeys = Array.isArray(runNode.attrs?.runPropertiesStyleKeys)
      ? runNode.attrs.runPropertiesStyleKeys
      : [];
    const currentOverrideKeys = Array.isArray(runNode.attrs?.runPropertiesOverrideKeys)
      ? runNode.attrs.runPropertiesOverrideKeys
      : [];
    const hasInlineOwnershipMetadata = Array.isArray(runNode.attrs?.runPropertiesInlineKeys);
    let currentInlineKeys: string[];
    if (hasInlineOwnershipMetadata) {
      currentInlineKeys = runNode.attrs.runPropertiesInlineKeys;
    } else if (currentStyleKeys.length > 0) {
      currentInlineKeys = [
        ...new Set([
          ...Object.keys(currentRunProperties).filter((key) => !currentStyleKeys.includes(key)),
          ...currentOverrideKeys,
        ]),
      ];
    } else {
      currentInlineKeys = Object.keys(currentRunProperties);
    }

    const nextRunProperties = { ...currentRunProperties };
    let runChanged = false;
    const changedRunPropertyKeys = new Set<string>();

    for (const [runPropertyKey, patchValue] of Object.entries(updates)) {
      const existingValue = nextRunProperties[runPropertyKey];
      const mergeResult = mergeRunAttributeValue(existingValue, patchValue);
      if (!mergeResult.changed) continue;

      if (mergeResult.nextValue === undefined) {
        delete nextRunProperties[runPropertyKey];
      } else {
        nextRunProperties[runPropertyKey] = mergeResult.nextValue;
      }

      runChanged = true;
      changedRunPropertyKeys.add(runPropertyKey);
    }

    if (!runChanged) continue;

    const normalizedNextRunProperties = Object.keys(nextRunProperties).length > 0 ? nextRunProperties : null;
    const nextInlineKeys = [
      ...new Set([
        ...currentInlineKeys.filter((key) => normalizedNextRunProperties && key in normalizedNextRunProperties),
        ...[...changedRunPropertyKeys].filter(
          (key) => normalizedNextRunProperties && key in normalizedNextRunProperties,
        ),
      ]),
    ];
    const nextOverrideKeys = [
      ...new Set([
        ...currentOverrideKeys.filter(
          (key) => normalizedNextRunProperties && key in normalizedNextRunProperties && currentStyleKeys.includes(key),
        ),
        ...[...changedRunPropertyKeys].filter(
          (key) => normalizedNextRunProperties && key in normalizedNextRunProperties && currentStyleKeys.includes(key),
        ),
      ]),
    ];
    const fullRunSelected = patchFrom === runContentFrom && patchTo === runContentTo;

    if (fullRunSelected) {
      tr.setNodeMarkup(
        runPos,
        runNode.type,
        {
          ...runNode.attrs,
          runProperties: normalizedNextRunProperties,
          runPropertiesInlineKeys: nextInlineKeys.length ? nextInlineKeys : null,
          runPropertiesOverrideKeys: nextOverrideKeys.length ? nextOverrideKeys : null,
        },
        runNode.marks,
      );
      changed = true;
      continue;
    }

    const relativeFrom = patchFrom - runContentFrom;
    const relativeTo = patchTo - runContentFrom;
    if (relativeFrom === relativeTo) continue;

    const replacementRuns: ProseMirrorNode[] = [];

    if (relativeFrom > 0) {
      const leftContent = runNode.content.cut(0, relativeFrom);
      replacementRuns.push(runType.create(runNode.attrs, leftContent, runNode.marks));
    }

    const middleContent = runNode.content.cut(relativeFrom, relativeTo);
    replacementRuns.push(
      runType.create(
        {
          ...runNode.attrs,
          runProperties: normalizedNextRunProperties,
          runPropertiesInlineKeys: nextInlineKeys.length ? nextInlineKeys : null,
          runPropertiesOverrideKeys: nextOverrideKeys.length ? nextOverrideKeys : null,
        },
        middleContent,
        runNode.marks,
      ),
    );

    if (relativeTo < runNode.content.size) {
      const rightContent = runNode.content.cut(relativeTo, runNode.content.size);
      replacementRuns.push(runType.create(runNode.attrs, rightContent, runNode.marks));
    }

    // Pass node array directly so the transaction builds Fragment using its own
    // prosemirror-model instance (avoids cross-instance Fragment errors).
    tr.replaceWith(runPos, runPos + runNode.nodeSize, replacementRuns);
    changed = true;
  }

  return changed;
}

function applyInlinePatchToRange(
  editor: Editor,
  tr: Transaction,
  absFrom: number,
  absTo: number,
  inline: InlineRunPatch,
): boolean {
  if (absFrom >= absTo) return false;

  const { schema } = editor.state;
  let changed = false;

  for (const key of BOOLEAN_INLINE_MARK_KEYS) {
    const markType = schema.marks[key];
    const value = inline[key] as boolean | null | undefined;
    if (applyTriStateMarkPatch(tr, markType, absFrom, absTo, value)) {
      changed = true;
    }
  }

  if (applyUnderlinePatch(tr, schema.marks.underline, absFrom, absTo, inline.underline)) {
    changed = true;
  }

  if (applyHighlightPatch(tr, schema.marks.highlight, absFrom, absTo, inline.highlight)) {
    changed = true;
  }

  const textStylePatch: TextStylePatch = {};
  for (const key of TEXT_STYLE_KEYS) {
    if (inline[key] !== undefined) {
      (textStylePatch as Record<string, unknown>)[key] = inline[key];
    }
  }
  if (inline.caps !== undefined) {
    textStylePatch.textTransform = capsToTextTransform(inline.caps ?? null);
  }
  // When fontFamily is being set (not cleared) via the textStyle mark path,
  // tell the calculateInlineRunPropertiesPlugin to preserve the mark-derived
  // fontFamily rather than re-deriving it through the encodeMarksFromRPr
  // comparison (which can incorrectly drop it due to theme font normalization).
  // Only for non-null values — clearing fontFamily must not trigger preservation,
  // otherwise the plugin would copy the old value back from existingRunProperties.
  if (textStylePatch.fontFamily != null) {
    tr.setMeta(PRESERVE_RUN_PROPERTIES_META_KEY, ['fontFamily']);
  }

  if (applyTextStylePatch(tr, schema.marks.textStyle, absFrom, absTo, textStylePatch)) {
    changed = true;
  }

  const runAttributeUpdates = buildRunAttributeUpdates(inline);
  if (applyRunAttributePatch(tr, schema.nodes?.run, absFrom, absTo, runAttributeUpdates)) {
    changed = true;
  }

  return changed;
}

// ---------------------------------------------------------------------------
// Block-anchor position resolution for create operations
// ---------------------------------------------------------------------------

/**
 * Derives the anchor block ID for a create step from a compiled target.
 *
 * - range target → the target's blockId directly.
 * - span target  → first segment block for 'before', last segment block for 'after'.
 *   This implements B0 invariant 4: multi-block refs anchor at span boundaries.
 */
function resolveCreateAnchorBlockId(target: CompiledTarget, position: 'before' | 'after', stepId: string): string {
  if (target.kind === 'range') {
    return target.blockId;
  }

  const segments = target.segments;
  if (!segments.length) {
    throw planError('INVALID_INPUT', 'span target has no segments', stepId);
  }

  return position === 'before' ? segments[0].blockId : segments[segments.length - 1].blockId;
}

// ---------------------------------------------------------------------------
// Range target executors (single-block — existing behavior)
// ---------------------------------------------------------------------------

export function executeTextRewrite(
  editor: Editor,
  tr: Transaction,
  target: CompiledRangeTarget,
  step: TextRewriteStep,
  mapping: Mapping,
): { changed: boolean } {
  const finish = (changed: boolean): { changed: boolean } => {
    if (changed) markTrackedRewriteChanged(tr);
    return { changed };
  };
  const absFrom = mapping.map(target.absFrom);
  const absTo = mapping.map(target.absTo);

  const replacementText = getReplacementText(step.args.replacement);
  const marks = resolveMarksForRange(editor, target, step);
  // A rewrite range can start in a normal parent (a run) and span into a
  // `text*`-only inline container (e.g. total-page-number) that rejects
  // lineBreak. The edits below land at remapped positions whose parent may
  // differ from `absFrom`, so probe admission at each actual edit position
  // rather than once at the range start: a newline that lands in a restrictive
  // parent falls back to literal text (the export safety net still emits
  // <w:br/>). Probe on the current `tr` so prior in-loop steps are reflected.
  const lineBreakNodeType = editor.state.schema.nodes?.lineBreak;
  const parentAllowsLineBreakAt = (pos: number): boolean =>
    lineBreakNodeType ? parentAllowsNodeAt(tr, pos, lineBreakNodeType) : false;
  const replaceTextRange = (from: number, to: number, text: string, parentPos: number): void => {
    if (text.length === 0) {
      tr.delete(from, to);
      return;
    }

    const content = buildTextWithTabs(editor.state.schema, text, asProseMirrorMarks(marks), {
      parentAllowsLineBreak: parentAllowsLineBreakAt(parentPos),
    });
    tr.replaceWith(from, to, content);
  };
  const structuralRewrite = resolveStructuralRangeRewrite(tr.doc, absFrom, absTo, step);

  if (structuralRewrite) {
    const slice = buildReplacementParagraphSlice(
      editor,
      structuralRewrite.replacementBlocks,
      marks,
      structuralRewrite.paragraphAttrs,
      step.id,
      structuralRewrite.leadingWrappers,
      structuralRewrite.trailingWrappers,
      structuralRewrite.openStart,
      structuralRewrite.openEnd,
    );
    try {
      // Validate the structural replace against the current document before
      // mutating the transaction. This lets us fall back to inline rewrite in
      // containers that cannot host sibling paragraph nodes.
      tr.doc.replace(structuralRewrite.replaceFrom, structuralRewrite.replaceTo, slice);
      tr.replace(structuralRewrite.replaceFrom, structuralRewrite.replaceTo, slice);
      return finish(replacementText !== target.text);
    } catch (error) {
      debugTextRewrite('structural rewrite fell back to inline replacement', {
        replaceFrom: structuralRewrite.replaceFrom,
        replaceTo: structuralRewrite.replaceTo,
        openStart: structuralRewrite.openStart,
        openEnd: structuralRewrite.openEnd,
        replacementBlockCount: structuralRewrite.replacementBlocks.length,
        stepId: step.id,
        error: error instanceof Error ? error.message : String(error),
      });
      // Fall back to inline replacement when the surrounding content model
      // cannot accept multiple paragraph siblings for this textblock.
    }
  }

  // 1. Character-level prefix/suffix trim to narrow the replacement range.
  //    This handles cases where only a few characters differ (e.g., a "(" added
  //    before a URL, or "YoY" → "year over year") without replacing the full range.
  //    Tab nodes render as real '\t' so diffs align with caller-supplied replacement text.
  const originalText = textBetweenWithTabs(tr.doc, absFrom, absTo, '', '');
  const origLen = originalText.length;
  const replLen = replacementText.length;

  if (rangeTouchesTrackedReviewState(tr.doc, absFrom, absTo)) {
    replaceTextRange(absFrom, absTo, replacementText, absFrom);
    return finish(replacementText.length === 0 ? target.text.length > 0 : replacementText !== target.text);
  }

  let prefix = 0;
  while (prefix < origLen && prefix < replLen && originalText[prefix] === replacementText[prefix]) {
    prefix++;
  }
  if (prefix === origLen && prefix === replLen) {
    return finish(false); // texts are identical
  }
  let suffix = 0;
  while (
    suffix < origLen - prefix &&
    suffix < replLen - prefix &&
    originalText[origLen - 1 - suffix] === replacementText[replLen - 1 - suffix]
  ) {
    suffix++;
  }

  const trimmedFrom = charOffsetToDocPos(tr.doc, absFrom, absTo, prefix);
  const trimmedTo = charOffsetToDocPos(tr.doc, absFrom, absTo, origLen - suffix);
  const trimmedOld = originalText.slice(prefix, origLen - suffix);
  const trimmedNew = replacementText.slice(prefix, replLen - suffix);

  if (isTrackedRewriteBatchTransaction(tr)) {
    replaceTextRange(absFrom, absTo, replacementText, absFrom);
    return finish(replacementText !== target.text);
  }

  // 2. Word-level diff on the trimmed range for multi-word granularity.
  const wordChanges = getWordChanges(trimmedOld, trimmedNew);

  if (shouldUseCoarseSingleTrackedRewrite(tr, wordChanges.length)) {
    replaceTextRange(absFrom, absTo, replacementText, absFrom);
    return finish(replacementText !== target.text);
  }

  if (wordChanges.length > 1) {
    // Multiple word-level changes: apply each granularly.
    const doc = tr.doc;
    const baseSteps = tr.steps.length;
    const mapped = wordChanges.map((change) => {
      if (change.type === 'insert') {
        return { ...change, docPos: charOffsetToDocPos(doc, trimmedFrom, trimmedTo, change.insertAt) };
      }
      return {
        ...change,
        docFrom: charOffsetToDocPos(doc, trimmedFrom, trimmedTo, change.oldFrom),
        docTo: charOffsetToDocPos(doc, trimmedFrom, trimmedTo, change.oldTo),
      };
    });

    for (let i = 0; i < mapped.length; i++) {
      const change = mapped[i];
      const remap = (pos: number) => {
        for (let s = baseSteps; s < tr.steps.length; s++) {
          pos = tr.steps[s].getMap().map(pos);
        }
        return pos;
      };

      if (change.type === 'delete') {
        tr.delete(remap(change.docFrom), remap(change.docTo));
      } else if (change.type === 'insert') {
        const content = buildTextWithTabs(editor.state.schema, change.newText, asProseMirrorMarks(marks), {
          parentAllowsLineBreak: parentAllowsLineBreakAt(remap(change.docPos)),
        });
        tr.insert(remap(change.docPos), content);
      } else {
        const content = buildTextWithTabs(editor.state.schema, change.newText, asProseMirrorMarks(marks), {
          parentAllowsLineBreak: parentAllowsLineBreakAt(remap(change.docFrom)),
        });
        tr.replaceWith(remap(change.docFrom), remap(change.docTo), content);
      }
    }
  } else if (trimmedNew.length === 0) {
    // Pure deletion after trimming: a non-empty replacement whose new text is
    // fully contained in the old text's common prefix + suffix collapses to an
    // empty delta (e.g. "best endeavours to:" → "endeavours to:" leaves
    // trimmedNew === ""). This also covers removing a lineBreak, now that
    // lineBreak counts as "\n" in the diff. Delete the removed range rather than
    // building schema.text(''), which ProseMirror rejects for empty text.
    tr.delete(trimmedFrom, trimmedTo);
  } else {
    // 0 or 1 word change: replace just the trimmed range.
    const content = buildTextWithTabs(editor.state.schema, trimmedNew, asProseMirrorMarks(marks), {
      parentAllowsLineBreak: parentAllowsLineBreakAt(trimmedFrom),
    });
    tr.replaceWith(trimmedFrom, trimmedTo, content);
  }

  return finish(replacementText !== target.text);
}

/**
 * Resolve the marks an insertion at `absPos` should inherit.
 *
 * Falls back to PM's `$pos.marks()` for mocked test docs. In a real editor,
 * runs through `getFormattingStateAtPos` so super-editor's paragraph-level
 * and run-level `runProperties` (e.g. a bold-paragraph default) flow through
 * to the inserted content — otherwise inserts into a bold paragraph produce
 * unmarked text/tab nodes that export without `<w:rPr>`.
 */
function resolveInheritedMarksAt(editor: Editor, tr: Transaction, absPos: number): readonly ProseMirrorMark[] {
  try {
    const state = editor.state as unknown as { doc: { resolve?: unknown } };
    if (typeof state?.doc?.resolve !== 'function') {
      const $pos = tr.doc.resolve(absPos);
      return mergeDirectInsertTrackedInsertionMark(tr, absPos, $pos.marks());
    }
    const resolved = getFormattingStateAtPos(
      editor.state as unknown as import('prosemirror-state').EditorState,
      absPos,
      editor as unknown as undefined,
    );
    return mergeDirectInsertTrackedInsertionMark(tr, absPos, (resolved?.resolvedMarks as ProseMirrorMark[]) ?? []);
  } catch {
    const $pos = tr.doc.resolve(absPos);
    return mergeDirectInsertTrackedInsertionMark(tr, absPos, $pos.marks());
  }
}

function getSharedTrackedInsertionMarkAt(tr: Transaction, absPos: number): ProseMirrorMark | null {
  const maxPos = typeof tr.doc.content?.size === 'number' ? tr.doc.content.size : absPos;
  const boundedPos = Math.max(0, Math.min(maxPos, absPos));
  const $pos = tr.doc.resolve(boundedPos);
  const beforeInsert = $pos.nodeBefore?.marks?.find((mark) => mark.type?.name === TrackInsertMarkName) ?? null;
  const afterInsert = $pos.nodeAfter?.marks?.find((mark) => mark.type?.name === TrackInsertMarkName) ?? null;
  const beforeId = typeof beforeInsert?.attrs?.id === 'string' ? beforeInsert.attrs.id : null;
  const afterId = typeof afterInsert?.attrs?.id === 'string' ? afterInsert.attrs.id : null;

  if (!beforeInsert || !afterInsert || !beforeId || beforeId !== afterId) {
    return null;
  }

  return beforeInsert;
}

function mergeDirectInsertTrackedInsertionMark(
  tr: Transaction,
  absPos: number,
  marks: readonly ProseMirrorMark[],
): readonly ProseMirrorMark[] {
  if (tr.getMeta?.('skipTrackChanges') !== true) {
    return marks;
  }

  const sharedInsert = getSharedTrackedInsertionMarkAt(tr, absPos);
  if (!sharedInsert) {
    return marks;
  }

  const sharedInsertId = typeof sharedInsert.attrs?.id === 'string' ? sharedInsert.attrs.id : null;
  if (
    sharedInsertId &&
    marks.some((mark) => mark.type?.name === TrackInsertMarkName && mark.attrs?.id === sharedInsertId)
  ) {
    return marks;
  }

  return [...marks, sharedInsert];
}

export function executeTextInsert(
  editor: Editor,
  tr: Transaction,
  target: CompiledRangeTarget,
  step: TextInsertStep,
  mapping: Mapping,
): { changed: boolean } {
  const position = step.args.position;
  const absPos = mapping.map(position === 'before' ? target.absFrom : target.absTo);

  const text = step.args.content.text;
  if (!text) return { changed: false };

  let marks: readonly ProseMirrorMark[] = [];
  const stylePolicy = step.args.style?.inline;
  if (stylePolicy?.mode === 'set') {
    marks = buildMarksFromSetMarks(editor, stylePolicy.setMarks);
  } else if (stylePolicy?.mode === 'clear') {
    marks = [];
  } else {
    marks = resolveInheritedMarksAt(editor, tr, absPos);
  }

  const structuralInsert = resolveStructuralTextInsert(tr.doc, absPos, step);
  if (structuralInsert) {
    const slice = buildReplacementParagraphSlice(
      editor,
      structuralInsert.replacementBlocks,
      marks,
      structuralInsert.paragraphAttrs,
      step.id,
      structuralInsert.leadingWrappers,
      structuralInsert.trailingWrappers,
      structuralInsert.openStart,
      structuralInsert.openEnd,
    );

    try {
      tr.doc.replace(structuralInsert.insertAt, structuralInsert.insertAt, slice);
      tr.replace(structuralInsert.insertAt, structuralInsert.insertAt, slice);
      return { changed: true };
    } catch (error) {
      debugTextRewrite('structural insert fell back to inline insertion', {
        insertAt: structuralInsert.insertAt,
        openStart: structuralInsert.openStart,
        openEnd: structuralInsert.openEnd,
        replacementBlockCount: structuralInsert.replacementBlocks.length,
        stepId: step.id,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  const tabNodeType = editor.state.schema.nodes?.tab;
  const parentAllowsTab = tabNodeType && text.includes('\t') ? parentAllowsNodeAt(tr, absPos, tabNodeType) : false;
  // Restrictive parents like total-page-number are `text*` and reject lineBreak;
  // probe admission so newline-bearing inserts fall back to literal text there.
  const lineBreakNodeType = editor.state.schema.nodes?.lineBreak;
  const parentAllowsLineBreak =
    lineBreakNodeType && /[\r\n]/.test(text) ? parentAllowsNodeAt(tr, absPos, lineBreakNodeType) : false;
  tr.insert(absPos, buildTextWithTabs(editor.state.schema, text, marks, { parentAllowsTab, parentAllowsLineBreak }));

  return { changed: true };
}

export function executeTextDelete(
  _editor: Editor,
  tr: Transaction,
  target: CompiledRangeTarget,
  _step: TextDeleteStep,
  mapping: Mapping,
): { changed: boolean } {
  const absFrom = mapping.map(target.absFrom);
  const absTo = mapping.map(target.absTo);

  if (absFrom === absTo) return { changed: false };

  tr.delete(absFrom, absTo);
  return { changed: true };
}

/**
 * Applies alignment to the paragraph node(s) that contain the given range.
 * Uses the same mechanism as paragraphsSetAlignmentWrapper: updates
 * paragraphProperties.justification via tr.setNodeMarkup.
 */
function applyAlignmentToRange(
  editor: Editor,
  tr: Transaction,
  absFrom: number,
  absTo: number,
  alignment: string,
): boolean {
  if (!alignment) return false;

  let changed = false;
  const doc = tr.doc;

  doc.nodesBetween(absFrom, absTo, (node, pos) => {
    // Only set alignment on textblock nodes (paragraphs, headings)
    if (!node.isTextblock) return;

    const existing = (node.attrs as Record<string, unknown>).paragraphProperties as Record<string, unknown> | undefined;
    const paragraphPos = typeof tr.doc.resolve === 'function' ? tr.doc.resolve(pos) : null;
    const resolved = calculateResolvedParagraphProperties(editor, node, paragraphPos as any);
    const justification = mapAlignmentToJustificationForParagraph(alignment as any, resolved?.rightToLeft === true);
    const currentJustification = existing?.justification;

    if (currentJustification === justification) return;

    const updated = { ...(existing ?? {}), justification };
    tr.setNodeMarkup(pos, undefined, { ...node.attrs, paragraphProperties: updated });
    changed = true;
  });

  return changed;
}

/**
 * Expands a position range to cover the full content of all textblock nodes
 * that overlap with it. Used when scope: "block" is set on a format.apply step.
 */
function expandToBlockBoundaries(
  doc: import('prosemirror-model').Node,
  from: number,
  to: number,
): { from: number; to: number } {
  let expandedFrom = from;
  let expandedTo = to;

  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isTextblock) return;
    const blockContentStart = pos + 1;
    const blockContentEnd = pos + node.nodeSize - 1;
    expandedFrom = Math.min(expandedFrom, blockContentStart);
    expandedTo = Math.max(expandedTo, blockContentEnd);
  });

  return { from: expandedFrom, to: expandedTo };
}

export function executeStyleApply(
  editor: Editor,
  tr: Transaction,
  target: CompiledRangeTarget,
  step: StyleApplyStep,
  mapping: Mapping,
): { changed: boolean } {
  let absFrom = mapping.map(target.absFrom);
  let absTo = mapping.map(target.absTo);

  // Expand to full block boundaries when scope is "block"
  if (step.args.scope === 'block') {
    const expanded = expandToBlockBoundaries(tr.doc, absFrom, absTo);
    absFrom = expanded.from;
    absTo = expanded.to;
  }

  let changed = false;

  if (step.args.inline) {
    changed = applyInlinePatchToRange(editor, tr, absFrom, absTo, step.args.inline) || changed;
  }

  if (step.args.alignment) {
    changed = applyAlignmentToRange(editor, tr, absFrom, absTo, step.args.alignment) || changed;
  }

  return { changed };
}

// ---------------------------------------------------------------------------
// Span target executors (cross-block)
// ---------------------------------------------------------------------------

/**
 * Validates that mapped span segments are still contiguous and in order.
 * Fails with SPAN_FRAGMENTED if a prior step has disrupted the span.
 */
function validateMappedSpanContiguity(target: CompiledSpanTarget, mapping: Mapping, stepId: string): void {
  let lastMappedEnd = -1;
  let lastOriginalEnd = -1;

  for (const seg of target.segments) {
    const mappedFrom = mapping.map(seg.absFrom, 1);
    const mappedTo = mapping.map(seg.absTo, -1);

    if (mappedFrom > mappedTo) {
      throw planError(
        'SPAN_FRAGMENTED',
        `span target "${target.matchId}" has been fragmented by a prior mutation step`,
        stepId,
        { matchId: target.matchId },
      );
    }

    if (lastMappedEnd >= 0) {
      if (mappedFrom < lastMappedEnd) {
        throw planError(
          'SPAN_FRAGMENTED',
          `span target "${target.matchId}" has been fragmented by a prior mutation step`,
          stepId,
          { matchId: target.matchId },
        );
      }

      const expectedGap = seg.absFrom - lastOriginalEnd;
      const actualGap = mappedFrom - lastMappedEnd;
      if (actualGap !== expectedGap) {
        throw planError(
          'SPAN_FRAGMENTED',
          `span target "${target.matchId}" has been fragmented by a prior mutation step`,
          stepId,
          { matchId: target.matchId },
        );
      }
    }

    lastMappedEnd = mappedTo;
    lastOriginalEnd = seg.absTo;
  }
}

export function executeSpanTextRewrite(
  editor: Editor,
  tr: Transaction,
  target: CompiledSpanTarget,
  step: TextRewriteStep,
  mapping: Mapping,
): { changed: boolean } {
  validateMappedSpanContiguity(target, mapping, step.id);

  const replacementBlocks = resolveReplacementBlocks(step.args.replacement, step.id);
  const policy = step.args.style?.inline ?? DEFAULT_INLINE_POLICY;

  // Replace the entire span (first segment start → last segment end)
  const firstSeg = target.segments[0];
  const lastSeg = target.segments[target.segments.length - 1];
  const absFrom = mapping.map(firstSeg.absFrom, 1);
  const absTo = mapping.map(lastSeg.absTo, -1);

  // Build replacement content: one text node per block, separated by paragraph nodes
  // For single replacement block, use flat replacement into the span
  if (replacementBlocks.length === 1) {
    if (target.segments.length === 1 && replacementBlocks[0] === target.text) return { changed: false };

    const marks = resolveSpanMarks(editor, target, policy, step.id);
    // Probe parent admission so a newline replacement into a `text*`-only span
    // parent falls back to literal text (the export safety net handles the rest).
    const lineBreakNodeType = editor.state.schema.nodes?.lineBreak;
    const parentAllowsLineBreak = lineBreakNodeType ? parentAllowsNodeAt(tr, absFrom, lineBreakNodeType) : false;
    const content = buildTextWithTabs(editor.state.schema, replacementBlocks[0], asProseMirrorMarks(marks), {
      parentAllowsLineBreak,
    });
    tr.replaceWith(absFrom, absTo, content);
    markTrackedRewriteChanged(tr);
    return { changed: true };
  }

  // Multi-block replacement: build paragraph nodes
  const { schema } = editor.state;
  const paragraphType = schema.nodes.paragraph;
  if (!paragraphType) {
    throw planError('INVALID_INPUT', 'paragraph node type not in schema', step.id);
  }

  const nodes: ProseMirrorNode[] = [];
  for (let i = 0; i < replacementBlocks.length; i++) {
    const segmentIndex = Math.min(i, target.segments.length - 1);
    const marks = resolveSegmentMarks(editor, target, segmentIndex, policy, step.id);
    const paragraphAttrs = resolveInheritedParagraphAttrsForReplacement(editor, target, segmentIndex);

    const text = replacementBlocks[i];
    const content = text.length > 0 ? buildTextWithTabs(schema, text, asProseMirrorMarks(marks)) : null;
    const para =
      paragraphType.createAndFill(paragraphAttrs, content ?? undefined) ??
      paragraphType.create(paragraphAttrs, content ?? undefined);
    nodes.push(para);
  }

  const slice = new Slice(Fragment.from(nodes), 1, 1);
  tr.replace(absFrom, absTo, slice);

  markTrackedRewriteChanged(tr);
  return { changed: true };
}

export function executeSpanTextDelete(
  _editor: Editor,
  tr: Transaction,
  target: CompiledSpanTarget,
  step: TextDeleteStep,
  mapping: Mapping,
): { changed: boolean } {
  validateMappedSpanContiguity(target, mapping, step.id);

  const firstSeg = target.segments[0];
  const lastSeg = target.segments[target.segments.length - 1];
  const absFrom = mapping.map(firstSeg.absFrom, 1);
  const absTo = mapping.map(lastSeg.absTo, -1);

  if (absFrom === absTo) return { changed: false };

  tr.delete(absFrom, absTo);
  return { changed: true };
}

export function executeSpanStyleApply(
  editor: Editor,
  tr: Transaction,
  target: CompiledSpanTarget,
  step: StyleApplyStep,
  mapping: Mapping,
): { changed: boolean } {
  validateMappedSpanContiguity(target, mapping, step.id);

  // Apply marks uniformly across the full span
  const firstSeg = target.segments[0];
  const lastSeg = target.segments[target.segments.length - 1];
  let absFrom = mapping.map(firstSeg.absFrom, 1);
  let absTo = mapping.map(lastSeg.absTo, -1);

  if (step.args.scope === 'block') {
    const expanded = expandToBlockBoundaries(tr.doc, absFrom, absTo);
    absFrom = expanded.from;
    absTo = expanded.to;
  }

  let changed = false;

  if (step.args.inline) {
    changed = applyInlinePatchToRange(editor, tr, absFrom, absTo, step.args.inline) || changed;
  }

  if (step.args.alignment) {
    changed = applyAlignmentToRange(editor, tr, absFrom, absTo, step.args.alignment) || changed;
  }

  return { changed };
}

// ---------------------------------------------------------------------------
// Replacement helpers
// ---------------------------------------------------------------------------

/** Extract flat replacement text from the payload (for single-block range targets). */
function getReplacementText(replacement: ReplacementPayload): string {
  if (replacement.blocks !== undefined) {
    return replacement.blocks.map((b) => b.text).join('\n\n');
  }
  if (replacement.text == null) {
    throw planError('INVALID_INPUT', 'replacement must specify either text or blocks');
  }
  return replacement.text;
}

/** Resolve replacement into an array of paragraph text strings. */
function resolveReplacementBlocks(replacement: ReplacementPayload, stepId: string): string[] {
  if (replacement.blocks !== undefined) {
    if (replacement.blocks.length === 0) {
      throw planError('INVALID_INPUT', 'replacement.blocks must contain at least one entry', stepId);
    }
    return replacement.blocks.map((b) => b.text);
  }

  // Flat text → normalize via D3 rules for span targets
  if (replacement.text == null) {
    throw planError('INVALID_INPUT', 'replacement must specify either text or blocks', stepId);
  }
  return normalizeReplacementText(replacement.text, stepId);
}

function resolveStructuredTextPayload(text: string, stepId: string): StructuredTextPayload {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const splitBefore = normalized.startsWith('\n');
  const splitAfter = normalized.endsWith('\n');
  const coreText = splitBefore || splitAfter ? normalized.replace(/^\n+/, '').replace(/\n+$/, '') : normalized;
  const blocks = coreText.length === 0 ? [''] : normalizeReplacementText(coreText, stepId);

  return {
    blocks,
    splitBefore,
    splitAfter,
  };
}

function resolveStructuredReplacementPayload(replacement: ReplacementPayload, stepId: string): StructuredTextPayload {
  if (replacement.blocks !== undefined) {
    if (replacement.blocks.length === 0) {
      throw planError('INVALID_INPUT', 'replacement.blocks must contain at least one entry', stepId);
    }

    return {
      blocks: replacement.blocks.map((block) => block.text),
      splitBefore: false,
      splitAfter: false,
    };
  }

  if (replacement.text == null) {
    throw planError('INVALID_INPUT', 'replacement must specify either text or blocks', stepId);
  }

  return resolveStructuredTextPayload(replacement.text, stepId);
}

function payloadNeedsStructuralHandling(payload: StructuredTextPayload): boolean {
  return payload.blocks.length > 1 || payload.splitBefore || payload.splitAfter;
}

function findSharedTextblockDepth(
  $from: ReturnType<ProseMirrorNode['resolve']>,
  $to: ReturnType<ProseMirrorNode['resolve']>,
): number | null {
  const maxDepth = Math.min($from.depth, $to.depth);
  for (let depth = maxDepth; depth >= 0; depth -= 1) {
    if ($from.node(depth) !== $to.node(depth)) {
      continue;
    }

    if ($from.node(depth)?.isTextblock) {
      return depth;
    }
  }

  return null;
}

function findAddressableInlineRangeWithinTextblock(
  doc: ProseMirrorNode,
  from: number,
  to: number,
): { from: number; to: number } | null {
  let inlineFrom: number | null = null;
  let inlineTo: number | null = null;

  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isInline) {
      return;
    }

    if (node.isText && node.text) {
      const nodeFrom = Math.max(from, pos);
      const nodeTo = Math.min(to, pos + node.text.length);
      if (nodeFrom >= nodeTo) {
        return;
      }

      if (inlineFrom === null) {
        inlineFrom = nodeFrom;
      }
      inlineTo = nodeTo;
      return;
    }

    if (!node.isLeaf && !node.isAtom) {
      return;
    }

    const nodeFrom = Math.max(from, pos);
    const nodeTo = Math.min(to, pos + node.nodeSize);
    if (nodeFrom >= nodeTo) {
      return;
    }

    if (inlineFrom === null) {
      inlineFrom = nodeFrom;
    }
    inlineTo = nodeTo;
  });

  return inlineFrom === null || inlineTo === null ? null : { from: inlineFrom, to: inlineTo };
}

function resolveInlineWrapperChainAt(doc: ProseMirrorNode, pos: number, textblockDepth: number): InlineWrapperSpec[] {
  const $pos = doc.resolve(pos);
  const chain: InlineWrapperSpec[] = [];

  for (let depth = textblockDepth + 1; depth <= $pos.depth; depth += 1) {
    const node = $pos.node(depth);
    if (!node?.isInline || node.isText) {
      continue;
    }

    chain.push({
      type: node.type,
      attrs: { ...(node.attrs ?? {}) },
      marks: node.marks,
    });
  }

  return chain;
}

/**
 * Strips identity attributes that must not be copied to replacement paragraphs.
 * Shared by both range and span replacement paths to avoid drift.
 */
function stripIdentityAttrs(attrs: Record<string, unknown>): Record<string, unknown> | null {
  const cloned = { ...attrs };
  delete cloned.paraId;
  delete cloned.sdBlockId;
  delete cloned.nodeId;
  delete cloned.id;
  delete cloned.blockId;
  delete cloned.uuid;
  return Object.keys(cloned).length > 0 ? cloned : null;
}

function resolveStructuralRangeRewrite(
  doc: ProseMirrorNode,
  absFrom: number,
  absTo: number,
  step: TextRewriteStep,
): {
  replacementBlocks: string[];
  paragraphAttrs: Record<string, unknown> | null;
  replaceFrom: number;
  replaceTo: number;
  openStart: number;
  openEnd: number;
  leadingWrappers: InlineWrapperSpec[];
  trailingWrappers: InlineWrapperSpec[];
} | null {
  if (absFrom === absTo) {
    return null;
  }

  const payload = resolveStructuredReplacementPayload(step.args.replacement, step.id);
  if (!payloadNeedsStructuralHandling(payload)) {
    return null;
  }

  const $from = doc.resolve(absFrom);
  const $to = doc.resolve(absTo);
  const textblockDepth = findSharedTextblockDepth($from, $to);
  if (textblockDepth === null) {
    debugTextRewrite('structural rewrite skipped: no shared textblock', { absFrom, absTo, stepId: step.id });
    return null;
  }

  // A "whole paragraph" text rewrite needs to cover the full addressable inline
  // content inside the textblock. We cannot use raw content boundaries here
  // because real documents often wrap text in inline containers like `run`,
  // which shifts text positions inward from the paragraph's content edges.
  const textblockStart = $from.start(textblockDepth);
  const textblockEnd = $from.end(textblockDepth);
  const inlineRange = findAddressableInlineRangeWithinTextblock(doc, textblockStart, textblockEnd);
  const replacesEntireTextblock = inlineRange !== null && absFrom <= inlineRange.from && absTo >= inlineRange.to;
  const selectionStaysWithinTextblock = inlineRange !== null && absFrom >= inlineRange.from && absTo <= inlineRange.to;

  if (!replacesEntireTextblock && !selectionStaysWithinTextblock) {
    debugTextRewrite('structural rewrite skipped: selection does not cover full inline range', {
      absFrom,
      absTo,
      textblockDepth,
      textblockType: $from.node(textblockDepth).type.name,
      textblockStart,
      textblockEnd,
      inlineRange,
      stepId: step.id,
    });
    return null;
  }

  const effectiveBlocks = [...payload.blocks];
  if (payload.splitBefore) {
    effectiveBlocks.unshift('');
  }
  if (payload.splitAfter) {
    effectiveBlocks.push('');
  }

  if (replacesEntireTextblock) {
    if (effectiveBlocks.length <= 1) {
      debugTextRewrite('structural rewrite skipped: whole-textblock rewrite resolved to one block', {
        replacementBlocks: effectiveBlocks,
        stepId: step.id,
      });
      return null;
    }

    const replaceFrom = $from.before(textblockDepth);
    const replaceTo = $from.after(textblockDepth);
    debugTextRewrite('structural rewrite enabled', {
      absFrom,
      absTo,
      textblockDepth,
      textblockType: $from.node(textblockDepth).type.name,
      inlineRange,
      replaceFrom,
      replaceTo,
      replacementBlockCount: effectiveBlocks.length,
      openStart: 0,
      openEnd: 0,
      stepId: step.id,
    });

    return {
      replacementBlocks: effectiveBlocks,
      paragraphAttrs: stripIdentityAttrs($from.node(textblockDepth).attrs as Record<string, unknown>),
      replaceFrom,
      replaceTo,
      openStart: 0,
      openEnd: 0,
      leadingWrappers: [],
      trailingWrappers: [],
    };
  }

  const leadingWrappers = resolveInlineWrapperChainAt(doc, absFrom, textblockDepth);
  const trailingWrappers = resolveInlineWrapperChainAt(doc, absTo, textblockDepth);
  const openStart = 1 + leadingWrappers.length;
  const openEnd = 1 + trailingWrappers.length;
  debugTextRewrite('structural rewrite enabled', {
    absFrom,
    absTo,
    textblockDepth,
    textblockType: $from.node(textblockDepth).type.name,
    inlineRange,
    replaceFrom: absFrom,
    replaceTo: absTo,
    replacementBlockCount: effectiveBlocks.length,
    openStart,
    openEnd,
    leadingWrapperDepth: leadingWrappers.length,
    trailingWrapperDepth: trailingWrappers.length,
    stepId: step.id,
  });

  return {
    replacementBlocks: effectiveBlocks,
    paragraphAttrs: stripIdentityAttrs($from.node(textblockDepth).attrs as Record<string, unknown>),
    replaceFrom: absFrom,
    replaceTo: absTo,
    openStart,
    openEnd,
    leadingWrappers,
    trailingWrappers,
  };
}

function resolveStructuralTextInsert(
  doc: ProseMirrorNode,
  absPos: number,
  step: TextInsertStep,
): {
  replacementBlocks: string[];
  paragraphAttrs: Record<string, unknown> | null;
  insertAt: number;
  openStart: number;
  openEnd: number;
  leadingWrappers: InlineWrapperSpec[];
  trailingWrappers: InlineWrapperSpec[];
} | null {
  const text = step.args.content.text;
  if (!text) {
    return null;
  }

  const payload = resolveStructuredTextPayload(text, step.id);
  if (!payloadNeedsStructuralHandling(payload)) {
    return null;
  }

  const $pos = doc.resolve(absPos);
  const textblockDepth = findSharedTextblockDepth($pos, $pos);
  if (textblockDepth === null) {
    debugTextRewrite('structural insert skipped: no shared textblock', { absPos, stepId: step.id });
    return null;
  }

  const wrappers = resolveInlineWrapperChainAt(doc, absPos, textblockDepth);
  const effectiveBlocks = [...payload.blocks];
  if (payload.splitBefore) {
    effectiveBlocks.unshift('');
  }
  if (payload.splitAfter) {
    effectiveBlocks.push('');
  }
  const openStart = 1 + wrappers.length;
  const openEnd = 1 + wrappers.length;
  debugTextRewrite('structural insert enabled', {
    absPos,
    textblockDepth,
    textblockType: $pos.node(textblockDepth).type.name,
    replacementBlockCount: effectiveBlocks.length,
    openStart,
    openEnd,
    wrapperDepth: wrappers.length,
    stepId: step.id,
  });

  return {
    replacementBlocks: effectiveBlocks,
    paragraphAttrs: stripIdentityAttrs($pos.node(textblockDepth).attrs as Record<string, unknown>),
    insertAt: absPos,
    openStart,
    openEnd,
    leadingWrappers: wrappers,
    trailingWrappers: wrappers,
  };
}

function buildReplacementParagraphNodes(
  editor: Editor,
  replacementBlocks: string[],
  marks: readonly unknown[],
  paragraphAttrs: Record<string, unknown> | null,
  stepId: string,
  leadingWrappers: InlineWrapperSpec[],
  trailingWrappers: InlineWrapperSpec[],
): ProseMirrorNode[] {
  const { schema } = editor.state;
  const paragraphType = schema.nodes.paragraph;
  if (!paragraphType) {
    throw planError('INVALID_INPUT', 'paragraph node type not in schema', stepId);
  }

  const wrapInlineContent = (
    contentNode: ProseMirrorNode | Fragment | null,
    wrappers: InlineWrapperSpec[],
  ): ProseMirrorNode => {
    let content: ProseMirrorNode | Fragment | null = contentNode;

    for (let index = wrappers.length - 1; index >= 0; index -= 1) {
      const wrapper = wrappers[index];
      content =
        wrapper.type.createAndFill(wrapper.attrs, content ?? undefined, wrapper.marks) ??
        wrapper.type.create(wrapper.attrs, content ?? undefined, wrapper.marks);
    }

    if (!content || content instanceof Fragment) {
      throw planError('INVALID_INPUT', 'could not build inline wrapper content', stepId);
    }

    return content;
  };

  const defaultWrappers = leadingWrappers.length > 0 ? leadingWrappers : trailingWrappers;

  return replacementBlocks.map((text, index) => {
    const textContent = text.length > 0 ? buildTextWithTabs(schema, text, asProseMirrorMarks(marks)) : null;
    const wrappers =
      index === 0
        ? leadingWrappers.length > 0
          ? leadingWrappers
          : defaultWrappers
        : index === replacementBlocks.length - 1
          ? trailingWrappers.length > 0
            ? trailingWrappers
            : defaultWrappers
          : defaultWrappers;
    const content =
      textContent == null
        ? wrappers.length > 0
          ? wrapInlineContent(null, wrappers)
          : undefined
        : wrappers.length > 0
          ? wrapInlineContent(textContent, wrappers)
          : textContent;
    return (
      paragraphType.createAndFill(paragraphAttrs, content ?? undefined) ??
      paragraphType.create(paragraphAttrs, content ?? undefined)
    );
  });
}

function buildReplacementParagraphSlice(
  editor: Editor,
  replacementBlocks: string[],
  marks: readonly unknown[],
  paragraphAttrs: Record<string, unknown> | null,
  stepId: string,
  leadingWrappers: InlineWrapperSpec[],
  trailingWrappers: InlineWrapperSpec[],
  openStart: number,
  openEnd: number,
): Slice {
  const nodes = buildReplacementParagraphNodes(
    editor,
    replacementBlocks,
    marks,
    paragraphAttrs,
    stepId,
    leadingWrappers,
    trailingWrappers,
  );
  return new Slice(Fragment.from(nodes), openStart, openEnd);
}

function resolveInheritedParagraphAttrsForReplacement(
  editor: Editor,
  target: CompiledSpanTarget,
  segmentIndex: number,
): Record<string, unknown> | null {
  const sourceSegment = target.segments[Math.min(segmentIndex, target.segments.length - 1)];
  const index = getBlockIndex(editor);
  const candidate = index.candidates.find((c) => c.nodeId === sourceSegment.blockId);
  const sourceAttrs = candidate?.node?.attrs;

  if (!sourceAttrs || typeof sourceAttrs !== 'object') {
    return null;
  }

  return stripIdentityAttrs(sourceAttrs as Record<string, unknown>);
}

// ---------------------------------------------------------------------------
// Span style resolution (D5)
// ---------------------------------------------------------------------------

/** Resolve marks for a single-block replacement of a span target. */
function resolveSpanMarks(
  editor: Editor,
  target: CompiledSpanTarget,
  policy: import('@superdoc/document-api').InlineStylePolicy,
  stepId: string,
): readonly unknown[] {
  if (policy.mode === 'set') {
    return buildMarksFromSetMarks(editor, policy.setMarks);
  }
  if (policy.mode === 'clear') {
    return [];
  }

  // preserve/merge: weighted majority across all segments
  if (!target.capturedStyleBySegment?.length) return [];

  // Flatten all runs across segments for global majority
  const allRuns = target.capturedStyleBySegment.flatMap((cs) => cs.runs);
  const combined = { runs: allRuns, isUniform: allRuns.length <= 1 };
  return resolveInlineStyle(editor, combined, policy, stepId);
}

/** Resolve marks for a specific replacement block mapped to a source segment. */
function resolveSegmentMarks(
  editor: Editor,
  target: CompiledSpanTarget,
  segmentIndex: number,
  policy: import('@superdoc/document-api').InlineStylePolicy,
  stepId: string,
): readonly unknown[] {
  if (policy.mode === 'set') {
    return buildMarksFromSetMarks(editor, policy.setMarks);
  }
  if (policy.mode === 'clear') {
    return [];
  }

  if (!target.capturedStyleBySegment?.length) return [];

  const captured = target.capturedStyleBySegment[segmentIndex];
  if (!captured) return [];

  return resolveInlineStyle(editor, captured, policy, stepId);
}

// ---------------------------------------------------------------------------
// Assert step evaluation
// ---------------------------------------------------------------------------

function countTextMatches(text: string, pattern: string, mode: string, caseSensitive: boolean): number {
  if (mode === 'regex') {
    // Invalid or unsafe (ReDoS-prone) patterns preserve the existing assert behavior:
    // they count as zero matches and fail unless the expected count is zero.
    const result = compileSafeTextRegex(pattern, { caseSensitive });
    if (!result.ok) {
      return 0;
    }
    const matches = text.match(result.regex);
    return matches ? matches.length : 0;
  }

  const searchText = caseSensitive ? text : text.toLowerCase();
  const searchPattern = caseSensitive ? pattern : pattern.toLowerCase();
  let count = 0;
  let pos = 0;
  while (true) {
    const idx = searchText.indexOf(searchPattern, pos);
    if (idx === -1) break;
    count++;
    pos = idx + 1;
  }
  return count;
}

type AssertIndexCandidate = {
  node: ProseMirrorNode;
  pos: number;
  end: number;
  nodeType: Exclude<ReturnType<typeof mapBlockNodeType>, undefined>;
  nodeId: string;
};

type AssertIndex = {
  candidates: AssertIndexCandidate[];
  byId: Map<string, AssertIndexCandidate>;
  ambiguous: ReadonlySet<string>;
};

function asId(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function resolveAssertNodeId(node: ProseMirrorNode, mappedType: AssertIndexCandidate['nodeType']): string | undefined {
  const attrs = node.attrs ?? {};
  if (mappedType === 'paragraph' || mappedType === 'heading' || mappedType === 'listItem') {
    return asId(attrs.paraId) ?? asId(attrs.sdBlockId) ?? asId(attrs.nodeId);
  }
  return (
    asId(attrs.blockId) ??
    asId(attrs.id) ??
    asId(attrs.paraId) ??
    asId(attrs.uuid) ??
    asId(attrs.sdBlockId) ??
    asId(attrs.nodeId)
  );
}

function buildAssertIndex(doc: ProseMirrorNode): AssertIndex {
  const candidates: AssertIndexCandidate[] = [];
  const byId = new Map<string, AssertIndexCandidate>();
  const ambiguous = new Set<string>();

  function registerKey(key: string, candidate: AssertIndexCandidate): void {
    if (byId.has(key)) {
      ambiguous.add(key);
      byId.delete(key);
      return;
    }
    if (!ambiguous.has(key)) {
      byId.set(key, candidate);
    }
  }

  doc.descendants((node: ProseMirrorNode, pos: number) => {
    const nodeType = mapBlockNodeType(node);
    if (!nodeType) return true;
    const nodeId = resolveAssertNodeId(node, nodeType);
    if (!nodeId) return true;

    const candidate: AssertIndexCandidate = {
      node,
      pos,
      end: pos + node.nodeSize,
      nodeType,
      nodeId,
    };

    candidates.push(candidate);
    registerKey(`${nodeType}:${nodeId}`, candidate);

    if (nodeType === 'paragraph' || nodeType === 'heading' || nodeType === 'listItem') {
      const aliasId = asId(node.attrs?.sdBlockId);
      if (aliasId && aliasId !== nodeId) {
        registerKey(`${nodeType}:${aliasId}`, candidate);
      }
    }

    return true;
  });

  return { candidates, byId, ambiguous };
}

function resolveAssertScope(
  index: AssertIndex,
  select: AssertStep['where']['select'],
  within: AssertStep['where']['within'],
): { ok: true; range: { start: number; end: number } | undefined } | { ok: false } {
  if (!within) return { ok: true, range: undefined };
  const query: Query = { select, within };
  const scope = resolveWithinScope(index, query, []);
  if (!scope.ok) return { ok: false };
  return { ok: true, range: scope.range };
}

function countNodeMatchesInDoc(
  doc: ProseMirrorNode,
  selector: Exclude<AssertStep['where']['select'], { type: 'text' }>,
  within: AssertStep['where']['within'],
): number {
  const index = buildAssertIndex(doc);
  const scope = resolveAssertScope(index, selector, within);
  if (!scope.ok) return 0;

  if (selector.kind && selector.kind !== 'block') return 0;

  const scoped = scopeByRange(index.candidates, scope.range);
  let count = 0;
  for (const candidate of scoped) {
    if (selector.nodeType && candidate.nodeType !== selector.nodeType) continue;
    count++;
  }
  return count;
}

function resolveScopedTextForAssert(
  doc: ProseMirrorNode,
  selector: Extract<AssertStep['where']['select'], { type: 'text' }>,
  within: AssertStep['where']['within'],
): string {
  const index = buildAssertIndex(doc);
  const scope = resolveAssertScope(index, selector, within);
  if (!scope.ok) return '';
  if (!scope.range) return doc.textContent;

  return textBetweenWithTabs(doc, scope.range.start, scope.range.end, '\n', '\ufffc');
}

function executeAssertStep(
  _editor: Editor,
  tr: Transaction,
  step: AssertStep,
): { passed: boolean; actualCount: number } {
  const where = step.where;
  if (where.by !== 'select') {
    throw planError('INVALID_INPUT', `assert steps only support by: 'select'`, step.id);
  }

  const selector = where.select;
  if (selector.type !== 'text') {
    const count = countNodeMatchesInDoc(tr.doc, selector, where.within);
    return { passed: count === step.args.expectCount, actualCount: count };
  }

  const text = resolveScopedTextForAssert(tr.doc, selector, where.within);
  const pattern = selector.pattern;
  const mode = selector.mode ?? 'contains';
  const caseSensitive = selector.caseSensitive ?? false;
  const count = countTextMatches(text, pattern, mode, caseSensitive);
  return { passed: count === step.args.expectCount, actualCount: count };
}

// ---------------------------------------------------------------------------
// Domain step executors — create operations
// ---------------------------------------------------------------------------

export function executeCreateStep(
  editor: Editor,
  tr: Transaction,
  step: MutationStep,
  targets: CompiledTarget[],
  mapping: Mapping,
): StepOutcome {
  const target = targets[0];
  if (!target) {
    throw planError('INVALID_INPUT', `${step.op} step requires at least one target`, step.id);
  }

  const args = step.args as Record<string, unknown>;
  const position = (args.position as 'before' | 'after') ?? 'after';

  // Derive anchor block from target kind:
  //   range  → target.blockId directly
  //   span   → first segment for 'before', last segment for 'after'
  const anchorBlockId = resolveCreateAnchorBlockId(target, position, step.id);

  // Create ops use block-anchor semantics: insert at block boundaries, never mid-text.
  // target.from/target.to (text-model offsets) are intentionally ignored.
  const anchorPos = resolveBlockInsertionPos(editor, anchorBlockId, position, step.id);
  const pos = mapping.map(anchorPos);

  const paragraphType = editor.state.schema?.nodes?.paragraph;
  if (!paragraphType) {
    throw planError('INVALID_INPUT', 'paragraph node type not in schema', step.id);
  }

  const sdBlockId = args.sdBlockId as string | undefined;
  const text = (args.text as string) ?? '';
  const textContent = text.length > 0 ? buildTextWithTabs(editor.state.schema, text, undefined) : null;

  let attrs: Record<string, unknown> | undefined;
  if (step.op === 'create.heading') {
    const level = (args.level as number) ?? 1;
    attrs = {
      ...(sdBlockId ? { sdBlockId } : undefined),
      paragraphProperties: { styleId: `Heading${level}` },
    };
  } else {
    attrs = sdBlockId ? { sdBlockId } : undefined;
  }

  const node =
    paragraphType.createAndFill(attrs, textContent ?? undefined) ??
    paragraphType.create(attrs, textContent ?? undefined);

  if (!node) {
    throw planError('INVALID_INPUT', `could not create ${step.op} node`, step.id);
  }

  tr.insert(pos, node);

  // E1: Verify no duplicate block IDs after insertion
  assertNoPostInsertDuplicateIds(tr.doc, step.id);

  return {
    stepId: step.id,
    op: step.op,
    effect: 'changed',
    matchCount: 1,
    data: { domain: 'text', resolutions: [] } as TextStepData,
  };
}

// ---------------------------------------------------------------------------
// Block identity invariant check (Workstream E)
// ---------------------------------------------------------------------------

/**
 * Walks the post-mutation document and asserts no two blocks share the same
 * identity (paraId/sdBlockId/nodeId). Called after every create-step insertion.
 */
function assertNoPostInsertDuplicateIds(doc: ProseMirrorNode, stepId: string): void {
  const seen = new Set<string>();
  const duplicateSet = new Set<string>();

  doc.descendants((node: ProseMirrorNode) => {
    // Only check textblock nodes (paragraphs, headings) — skip containers (tables, blockquotes)
    if (!node.isTextblock) return true;
    const attrs = (node.attrs ?? {}) as Record<string, unknown>;
    const id =
      (typeof attrs.paraId === 'string' && attrs.paraId) ||
      (typeof attrs.sdBlockId === 'string' && attrs.sdBlockId) ||
      (typeof attrs.nodeId === 'string' && attrs.nodeId);

    if (!id) return true;

    if (seen.has(id)) {
      duplicateSet.add(id);
    } else {
      seen.add(id);
    }
    return true;
  });

  if (duplicateSet.size > 0) {
    const duplicates = [...duplicateSet];
    throw planError(
      'INTERNAL_ERROR',
      `create step produced duplicate block identities: [${duplicates.join(', ')}]`,
      stepId,
      {
        source: 'executor:checkPostInsertIdentityUniqueness',
        invariant: 'post-insert block IDs must be unique',
        duplicateBlockIds: duplicates,
      },
    );
  }
}

// ---------------------------------------------------------------------------
// Shared execution core — used by both executePlan and previewPlan
// ---------------------------------------------------------------------------

export function runMutationsOnTransaction(
  editor: Editor,
  tr: Transaction,
  compiled: CompiledPlan,
  options: { throwOnAssertFailure: boolean; changeMode?: 'direct' | 'tracked'; isPreview?: boolean },
): {
  stepOutcomes: StepOutcome[];
  assertFailures: Array<{ stepId: string; expectedCount: number; actualCount: number }>;
  commandDispatched: boolean;
} {
  const mapping = tr.mapping;
  const stepOutcomes: StepOutcome[] = [];
  const assertFailures: Array<{ stepId: string; expectedCount: number; actualCount: number }> = [];

  const ctx: ExecuteContext = {
    editor,
    tr,
    mapping,
    changeMode: options.changeMode ?? 'direct',
    planGroupId: '',
    commandDispatched: false,
    isPreview: options.isPreview ?? false,
  };

  for (const compiledStep of compiled.mutationSteps) {
    const { step, targets } = compiledStep;
    const executor = getStepExecutor(step.op);
    if (!executor) {
      throw planError('INVALID_INPUT', `unsupported step op "${step.op}"`, step.id);
    }
    const outcome = executor.execute(ctx, targets, step);
    stepOutcomes.push(outcome);
  }

  for (const assertStep of compiled.assertSteps) {
    const { passed, actualCount } = executeAssertStep(editor, tr, assertStep);

    if (!passed) {
      if (options.throwOnAssertFailure) {
        throw planError(
          'PRECONDITION_FAILED',
          `assert "${assertStep.id}" expected ${assertStep.args.expectCount} matches but found ${actualCount}`,
          assertStep.id,
          { expectedCount: assertStep.args.expectCount, actualCount },
        );
      }
      assertFailures.push({ stepId: assertStep.id, expectedCount: assertStep.args.expectCount, actualCount });
    }

    const data: AssertStepData = {
      domain: 'assert',
      expectedCount: assertStep.args.expectCount,
      actualCount,
    };

    stepOutcomes.push({
      stepId: assertStep.id,
      op: 'assert',
      effect: passed ? 'assert_passed' : 'assert_failed',
      matchCount: actualCount,
      data,
    });
  }

  return { stepOutcomes, assertFailures, commandDispatched: ctx.commandDispatched };
}

// ---------------------------------------------------------------------------
// Shared post-compilation execution
// ---------------------------------------------------------------------------

export interface ExecuteCompiledOptions {
  changeMode?: 'direct' | 'tracked';
  expectedRevision?: string;
}

export function executeCompiledPlan(
  editor: Editor,
  compiled: CompiledPlan,
  options: ExecuteCompiledOptions = {},
): PlanReceipt {
  const startTime = performance.now();
  const revisionBefore = getRevision(editor);

  checkRevision(editor, options.expectedRevision);

  // Close the current undo group so this API mutation becomes its own undo step,
  // preventing PM's newGroupDelay from merging sequential API calls.
  // The collab-history path requires both collaborationProvider AND ydoc (matching
  // the History extension guard at history.js:34); ydoc-without-provider uses PM history.
  if (editor.options?.collaborationProvider && editor.options?.ydoc) {
    try {
      yUndoPluginKey.getState(editor.state)?.undoManager?.stopCapturing();
    } catch {
      // yUndoPlugin may not be loaded — safe to ignore.
    }
  } else {
    try {
      editor.view?.dispatch?.(closeHistory(editor.state.tr));
    } catch {
      // History plugin may not be loaded — safe to ignore.
    }
  }

  // D3: Detect revision drift between compile and execute
  if (compiled.compiledRevision !== revisionBefore) {
    throw planError(
      'REVISION_CHANGED_SINCE_COMPILE',
      `Document revision changed between compile and execute. Compiled at "${compiled.compiledRevision}", now at "${revisionBefore}".`,
      undefined,
      {
        compiledRevision: compiled.compiledRevision,
        currentRevision: revisionBefore,
        stepCount: compiled.mutationSteps.length,
        failedAtStep: 'pre-execution',
        remediation: 'Re-compile the plan against the current document state.',
      },
    );
  }

  const tr = editor.state.tr;
  const changeMode = options.changeMode ?? 'direct';

  applyMutationPlanMeta(tr, compiled, changeMode);

  const { stepOutcomes } = runMutationsOnTransaction(editor, tr, compiled, {
    throwOnAssertFailure: true,
    changeMode,
    isPreview: false,
  });

  if (tr.docChanged) {
    editor.dispatch(tr);
  }

  const revisionAfter = getRevision(editor);
  const totalMs = performance.now() - startTime;

  return {
    success: true,
    revision: {
      before: revisionBefore,
      after: revisionAfter,
    },
    steps: stepOutcomes,
    timing: { totalMs },
  };
}

// ---------------------------------------------------------------------------
// Main execution entry point (selector-based plans)
// ---------------------------------------------------------------------------

export function executePlan(editor: Editor, input: MutationsApplyInput): PlanReceipt {
  if (!input.steps?.length) {
    throw planError('INVALID_INPUT', 'plan must contain at least one step');
  }

  // Reject stale optimistic-concurrency requests BEFORE compilePlan can
  // dispatch the identity-repair transaction. Without this, a caller's
  // mismatched expectedRevision would surface as REVISION_MISMATCH *after*
  // the repair already rewrote block identities on the doc — we'd reject
  // the user's intent but still leave the document mutated.
  checkRevision(editor, input.expectedRevision);

  const compiled = compilePlan(editor, input.steps, {
    selectTextModel: input.changeMode === 'tracked' ? 'raw' : 'visible',
  });

  return executeCompiledPlan(editor, compiled, {
    changeMode: input.changeMode ?? 'direct',
    expectedRevision: input.expectedRevision,
  });
}
